import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import { ColDef } from 'ag-grid-community';
import { CollectionView } from '@casops/components/layout/collection-view/CollectionView';
import { useTableData } from '@casops/shared/hooks/useTableData';
import {
  ListGeofenceStyleModel,
  useDeleteGeofenceStyleMutation,
  useListGeofenceStylesQuery,
} from '@casops/store/apis/geofenceStylesApi';
import { columnDefModelActions } from '@casops/components/tables/grid/columnDefs';
import { ModelActionsCellRendererParams } from '@casops/components/tables/cells/model-actions/ModelActionsCellRenderer';
import { Permission, useIsUserWithPermission } from '@casops/permissions';
import { SidePanel } from '@casops/store/slices/globalSlice';
import { ModelName } from '@casops/store/api';
import CreateItemButton from '@casops/components/inputs/create-item-button/CreateItemButton';
import { CellBadgeListRenderer } from '@casops/components/tables/cells/CellBadgeListRenderer';
import { getDefaultModelArg } from '@casops/components/tables/cells/model-actions/utils';

type Model = ListGeofenceStyleModel;

const modelName = ModelName.GeofenceStyle;
const sidePanel = SidePanel.ManageGeofenceStyle;
const permission = Permission.GeofenceManagement;

const canDeleteItem = (data: Model): boolean => !data.used;

const getItemName = (data: Model): string => data.name;

const cellRendererParams: ModelActionsCellRendererParams<Model> = {
  modelName,
  permission,
  editProps: {
    sidePanel,
    canEditItem: true,
  },
  deleteProps: {
    canDeleteItem,
    getQueryArg: getDefaultModelArg,
    useDeleteModelMutation: useDeleteGeofenceStyleMutation,
    getItemName,
  },
};

export const GeofenceStyles = () => {
  const { t } = useTranslation();

  const isAllowedModelActions = useIsUserWithPermission(permission);

  const columnDefs = useMemo<ColDef<Model>[]>(() => {
    const columnDefs: ColDef<Model>[] = [
      { field: 'name', headerName: t('Fields.StyleName'), minWidth: 100, maxWidth: 300 },
      {
        cellRenderer: CellBadgeListRenderer,
        field: 'geos',
        headerName: t('Fields.Geofences'),
        minWidth: 300,
      },
      { field: 'desc', headerName: t('Fields.Description'), minWidth: 100 },
    ];

    if (isAllowedModelActions) {
      columnDefs.push({
        ...columnDefModelActions<Model>(t, cellRendererParams),
        minWidth: 80,
        maxWidth: 120,
      });
    }

    return columnDefs;
  }, [t, isAllowedModelActions]);

  const noResultsDescription = t('Model.Collection.NoItems', { modelName });

  const { data, ...requestRemainder } = useListGeofenceStylesQuery(undefined, {
    refetchOnMountOrArgChange: 120,
  });

  const { hasData, noDataDescription } = useTableData<Model>({
    data,
    ...requestRemainder,
    noResultsDescription,
  });

  return (
    <CollectionView<Model>
      sliceKey={modelName}
      modelName={modelName}
      columnDefs={columnDefs}
      rowData={data}
      hasData={hasData}
      noDataDescription={noDataDescription}
      actions={
        <>
          <CreateItemButton modelName={modelName} sidePanel={sidePanel} permission={permission} />
          <CreateItemButton
            modelName={ModelName.GeofencePinDrop}
            sidePanel={SidePanel.ManagePinDropTemplate}
            permission={permission}
            label={t('Model.Title', { modelName: ModelName.GeofencePinDrop })}
            icon="edit"
            variant="secondary"
          />
        </>
      }
    />
  );
};

export default GeofenceStyles;
